import type { Bundle, Task } from "@medplum/fhirtypes";
import type { TaskNote } from "@/features/tasks";

// --- Types ---

export type ActivityEventKind =
  | "created"
  | "status-change"
  | "owner-change"
  | "priority-change"
  | "registration-review";

export interface ActivityEvent {
  kind: ActivityEventKind;
  timestamp: string;
  oldValue?: string;
  newValue?: string;
  oldDisplay?: string;
  newDisplay?: string;
  /** Set on a registration-review item: the task whose review page it opens. */
  taskId?: string;
}

export type TimelineEntry =
  | { type: "comment"; note: TaskNote; noteIndex: number }
  | { type: "event"; event: ActivityEvent };

// --- Priority labels (shared with $taskId.tsx) ---

export const PRIORITY_LABEL: Record<string, string> = {
  routine: "None",
  urgent: "Low",
  asap: "Medium",
  stat: "High",
};

// --- Valid app-level statuses (filter out FHIR-only statuses like "requested") ---

const VALID_TASK_STATUSES = new Set(["draft", "ready", "in-progress", "on-hold", "completed", "cancelled", "failed"]);

// --- Diff engine ---

export function diffTaskVersions(newer: Task, older: Task): ActivityEvent[] {
  const events: ActivityEvent[] = [];
  const timestamp = newer.meta?.lastUpdated ?? "";

  if (newer.status !== older.status) {
    events.push({
      kind: "status-change",
      timestamp,
      oldValue: older.status,
      newValue: newer.status,
    });
  }

  if (newer.owner?.reference !== older.owner?.reference) {
    events.push({
      kind: "owner-change",
      timestamp,
      oldValue: older.owner?.reference,
      newValue: newer.owner?.reference,
      oldDisplay: older.owner?.display,
      newDisplay: newer.owner?.display,
    });
  }

  if (newer.priority !== older.priority) {
    events.push({
      kind: "priority-change",
      timestamp,
      oldValue: older.priority,
      newValue: newer.priority,
    });
  }

  return events;
}

export function extractActivityEvents(bundle: Bundle<Task>): ActivityEvent[] {
  const entries = bundle.entry ?? [];
  // History bundles are reverse-chron — reverse to walk oldest first
  const versions = [...entries]
    .reverse()
    .map((e) => e.resource)
    .filter(Boolean) as Task[];

  if (versions.length === 0) return [];

  const events: ActivityEvent[] = [{ kind: "created", timestamp: versions[0].meta?.lastUpdated ?? "" }];

  for (let i = 1; i < versions.length; i++) {
    for (const ev of diffTaskVersions(versions[i], versions[i - 1])) {
      // Only show status changes between valid app statuses
      if (
        ev.kind === "status-change" &&
        (!VALID_TASK_STATUSES.has(ev.oldValue ?? "") || !VALID_TASK_STATUSES.has(ev.newValue ?? ""))
      ) {
        continue;
      }
      // Drop priority diffs that are semantically no-ops — e.g. $apply's
      // two-phase write where undefined → "routine" both render as "None".
      if (ev.kind === "priority-change" && (ev.oldValue ?? "routine") === (ev.newValue ?? "routine")) {
        continue;
      }
      events.push(ev);
    }
  }

  return events;
}

/**
 * Backfill display names on owner-change events when the historical Task
 * version stored only a reference (Task.owner.display unset). The map is keyed
 * by FHIR reference string (e.g. "Practitioner/<uuid>").
 */
export function enrichActivityEvents(events: ActivityEvent[], displayByRef: Map<string, string>): ActivityEvent[] {
  return events.map((ev) => {
    if (ev.kind !== "owner-change") return ev;
    const next: ActivityEvent = { ...ev };
    if (!next.oldDisplay && ev.oldValue) next.oldDisplay = displayByRef.get(ev.oldValue);
    if (!next.newDisplay && ev.newValue) next.newDisplay = displayByRef.get(ev.newValue);
    return next;
  });
}

// --- Merge ---

export function mergeTimeline(notes: TaskNote[], events: ActivityEvent[]): TimelineEntry[] {
  const entries: TimelineEntry[] = [
    ...notes.map((note, i) => ({ type: "comment" as const, note, noteIndex: i })),
    ...events.map((event) => ({ type: "event" as const, event })),
  ];

  entries.sort((a, b) => {
    const tA = a.type === "comment" ? (a.note.time ?? "") : a.event.timestamp;
    const tB = b.type === "comment" ? (b.note.time ?? "") : b.event.timestamp;
    return tA.localeCompare(tB);
  });

  return entries;
}
